import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { PRODUCT_OPTIONS } from "@/data/content";
import { waLink, mailLink } from "@/lib/contactLinks";
import { Send, Loader2, MessageCircle, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", country: "", email: "", whatsapp: "", product: "", quantity: "", packaging: "", destination: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#04121f] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/40 transition-colors";

export default function ExportInquiry({ id = "inquiry", heading = "Send an Export Inquiry", defaultProduct = "" }) {
  const [form, setForm] = useState({ ...EMPTY, product: defaultProduct });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.whatsapp || !form.product || !form.message) {
      toast.error("Please fill in name, email, WhatsApp/phone, product and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        company: form.company,
        country: form.country,
        phone: form.whatsapp,
        whatsapp: form.whatsapp,
        email: form.email,
        requirement_type: form.product,
        quantity: form.quantity,
        packaging: form.packaging,
        destination: form.destination,
        message: form.message,
      });
      toast.success("Thank you! Your export inquiry has been received. Our team will respond shortly.");
      setForm({ ...EMPTY, product: defaultProduct });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      id={id}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      className="rounded-3xl border border-white/10 bg-[#071728] p-6 sm:p-8 scroll-mt-24"
    >
      <h3 className="font-display text-xl sm:text-2xl font-bold text-white">{heading}</h3>
      <p className="mt-2 text-sm text-slate-400">Tell us your product, quantity, packaging preference and destination — we'll respond with relevant product and commercial information.</p>

      <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="export-inquiry-form">
        <div><label className="text-xs font-medium text-slate-300">Name *</label><input value={form.name} onChange={update("name")} placeholder="Full name" className={`mt-1.5 ${inputCls}`} data-testid="inq-name" /></div>
        <div><label className="text-xs font-medium text-slate-300">Company Name</label><input value={form.company} onChange={update("company")} placeholder="Company" className={`mt-1.5 ${inputCls}`} data-testid="inq-company" /></div>
        <div><label className="text-xs font-medium text-slate-300">Country</label><input value={form.country} onChange={update("country")} placeholder="Country" className={`mt-1.5 ${inputCls}`} data-testid="inq-country" /></div>
        <div><label className="text-xs font-medium text-slate-300">Email *</label><input type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" className={`mt-1.5 ${inputCls}`} data-testid="inq-email" /></div>
        <div><label className="text-xs font-medium text-slate-300">WhatsApp / Phone *</label><input value={form.whatsapp} onChange={update("whatsapp")} placeholder="+.. number" className={`mt-1.5 ${inputCls}`} data-testid="inq-whatsapp" /></div>
        <div>
          <label className="text-xs font-medium text-slate-300">Product Interested In *</label>
          <select value={form.product} onChange={update("product")} className={`mt-1.5 ${inputCls} appearance-none cursor-pointer`} data-testid="inq-product">
            <option value="" disabled>Select a product</option>
            {PRODUCT_OPTIONS.map((o) => <option key={o} value={o} className="bg-[#04121f]">{o}</option>)}
          </select>
        </div>
        <div><label className="text-xs font-medium text-slate-300">Required Quantity</label><input value={form.quantity} onChange={update("quantity")} placeholder="e.g. 5 MT / carton count" className={`mt-1.5 ${inputCls}`} data-testid="inq-quantity" /></div>
        <div><label className="text-xs font-medium text-slate-300">Packaging Requirement</label><input value={form.packaging} onChange={update("packaging")} placeholder="e.g. bulk cartons, private label" className={`mt-1.5 ${inputCls}`} data-testid="inq-packaging" /></div>
        <div className="sm:col-span-2"><label className="text-xs font-medium text-slate-300">Destination Port / City</label><input value={form.destination} onChange={update("destination")} placeholder="e.g. Jebel Ali, Dubai" className={`mt-1.5 ${inputCls}`} data-testid="inq-destination" /></div>
        <div className="sm:col-span-2"><label className="text-xs font-medium text-slate-300">Message *</label><textarea value={form.message} onChange={update("message")} rows={4} placeholder="Share your requirement details..." className={`mt-1.5 ${inputCls} resize-none`} data-testid="inq-message" /></div>

        <div className="sm:col-span-2 flex flex-col sm:flex-row flex-wrap gap-3">
          <button type="submit" disabled={submitting} data-testid="submit-export-inquiry" className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-500 px-7 py-3.5 text-sm font-semibold text-[#03121f] shadow-[0_10px_30px_rgba(0,240,255,0.3)] hover:-translate-y-0.5 transition-all disabled:opacity-60">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {submitting ? "Sending..." : "Submit Export Inquiry"}
          </button>
          <a href={waLink("Hello AquaFionix, I'd like to inquire about Makhana export supply.")} target="_blank" rel="noreferrer" data-testid="whatsapp-export-team" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-[#052e16] hover:-translate-y-0.5 transition-all">
            <MessageCircle className="h-4 w-4" /> WhatsApp Export Team
          </a>
          <a href={mailLink()} data-testid="email-export-team" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-all">
            <Mail className="h-4 w-4 text-cyan-300" /> Email Export Team
          </a>
        </div>
      </form>
    </motion.div>
  );
}
